import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import type { UserSummaryUpdatedProfileItemsEnum } from '~/api'

dayjs.extend(relativeTime)

export const NEW_USER_THRESHOLD_DAYS = 7
export const RECENT_UPDATE_THRESHOLD_DAYS = 7
export const UPDATED_USER_THRESHOLD_DAYS = 10

const UPDATED_ITEM_LABEL_MAP: Record<string, string> = {
  PORTFOLIO: 'ポートフォリオ',
  INTRODUCTION: '自己紹介',
  CAREER: 'キャリア',
  SKILL: 'スキル',
  DESIRED_CONDITION: '希望条件',
  OCCUPATION: '経験職種',
  ACHIEVEMENT: '受賞歴・資格',
}

export const isNewUser = (
  user: UserSummary,
  thresholdDays: number,
): boolean => {
  if (!user.registeredAt) return false
  return dayjs().diff(dayjs(user.registeredAt), 'day') <= thresholdDays
}

export const getUserLatestUpdate = (
  user: UserSummary,
  options: {
    intentionVisible: boolean
    updateType?: 'intention' | 'profile'
    thresholdDays?: number
  },
): { text: string; fromNow: string } | null => {
  const {
    intentionVisible,
    updateType,
    thresholdDays = RECENT_UPDATE_THRESHOLD_DAYS,
  } = options
  const isProfileUpdated =
    !!user.updatedProfileAt &&
    dayjs().diff(dayjs(user.updatedProfileAt), 'day') <= thresholdDays

  const isIntentionWorkUpdated =
    !!user.intentionWorkUpdatedAt &&
    dayjs().diff(dayjs(user.intentionWorkUpdatedAt), 'day') <= thresholdDays

  const isIntentionSideworkUpdated =
    !!user.intentionSideworkUpdatedAt &&
    dayjs().diff(dayjs(user.intentionSideworkUpdatedAt), 'day') <= thresholdDays

  // 転職意欲の更新情報は機能フラグで表示制御
  const isIntentionUpdated =
    intentionVisible &&
    (!!user.intentionWorkUpdatedAt || !!user.intentionSideworkUpdatedAt) &&
    dayjs().diff(dayjs(user.intentionWorkLastUpdatedAt), 'day') <= thresholdDays

  const updatedProfileText = (() => {
    if (!user.updatedProfileItems || user.updatedProfileItems.length === 0)
      return ''
    const items = user.updatedProfileItems.map(
      (code: UserSummaryUpdatedProfileItemsEnum) =>
        UPDATED_ITEM_LABEL_MAP[code],
    )
    return items.length > 1
      ? `${items.slice(0, 2).join('、')}などを更新`
      : `${items[0]}を更新`
  })()

  const intentionUpdatedText = (() => {
    const items = [
      isIntentionWorkUpdated && '転職意欲',
      isIntentionSideworkUpdated && '副業意欲',
    ].filter(Boolean)
    return items.length ? `${(items as string[]).join('、')}を更新` : ''
  })()

  const toResult = (text: string, date: string) => ({
    text,
    fromNow: dayjs(date).fromNow(),
  })

  const profileCandidate = isProfileUpdated
    ? { text: updatedProfileText, date: user.updatedProfileAt! }
    : null
  const intentionCandidate = isIntentionUpdated
    ? { text: intentionUpdatedText, date: user.intentionWorkLastUpdatedAt! }
    : null

  if (updateType === 'profile')
    return profileCandidate
      ? toResult(profileCandidate.text, profileCandidate.date)
      : null
  if (updateType === 'intention')
    return intentionCandidate
      ? toResult(intentionCandidate.text, intentionCandidate.date)
      : null

  const candidates = [profileCandidate, intentionCandidate].filter(Boolean) as {
    text: string
    date: string
  }[]

  if (!candidates.length) return null
  const winner = candidates.sort((a, b) => dayjs(b.date).diff(dayjs(a.date)))[0]
  return toResult(winner.text, winner.date)
}

export const getDisplayCareer = (
  user: UserSummary | GetUserDetail200ResponseUser,
): UserSummaryCareersInner => {
  return user.careers?.[0]
}

export const getUserDisplayCareerName = (
  career: UserSummaryCareersInner | undefined,
): string => {
  switch (career?.careerType) {
    case CareerType.COMPANY:
    case CareerType.SCHOOL:
      return career?.affiliationName || ''
    case CareerType.FREELANCE:
      return 'フリーランス'
    default:
      return ''
  }
}

export const getUserCareerTags = (
  user: UserSummary | GetUserDetail200ResponseUser,
): GetUserDetail200ResponseUserCareersInnerCareerDetailsInnerTagsInner[] => {
  return (
    user.careers?.flatMap(
      (career) =>
        career.careerDetails?.flatMap((detail) => detail.tags || []) || [],
    ) || []
  )
}

export const isInputtedUserSkill = (
  userSkill: GetUserDetail200ResponseUserUserSkillsInner,
) => {
  switch (userSkill.categoryType) {
    case UserSkillCategoryType.WILL_CAN:
      return userSkill.labels?.some(
        (label) => label.skillPoint > 0 || label.skillPointSub > 0,
      )
    case UserSkillCategoryType.OPPOSITE:
      return userSkill.labels?.some((label) => label.skillPoint !== 50)
    default:
      return false
  }
}
